
import * as types from "@/store/mutation-types";
import { claimsFormSchema } from "@/forms/shared/claims";
import {
  claimsMapActions,
  claimsMapMutations,
  claimsMapState
} from "@/store/modules/claims";
import Vue from "vue";
import { cloneDeep, get, merge, omit, set } from "lodash";
import {
  AtlasClaimRepresentativeOptions,
  FieldAdjusterOptions,
  KindOfLossOptions,
  LEGACY_PHP_API_DATE_FORMAT
} from "@/helpers/constants";
import * as dateFns from "date-fns";
import { processPaymentOptionEdit } from "@/helpers/claims";
import { authMapGetters } from "@/store/modules/auth";
import { downloadClaimsDocument } from "@/helpers/downloadLossForm";
import { getLossMortgageAddress } from "@/helpers/lossFormDataProcessor";
import LossFormClaimInfo from "@/components/lossForm/LossFormClaimInfo.vue";
import ActivitiesMixin from "@/components/ActivitiesView/ActivitiesMixin.vue";
import LossForm from "@/components/lossForm/LossForm.vue";
import CustomAlert from "@/components/CustomAlert/CustomAlert.vue";
import { lossDateIsGreaterThan120Days } from "@/helpers/generalHelpers";

interface IClaims {
  errorMessage: string;
  receivePaymentOption: string;
  hasValidCredentials: boolean;
  resourceNotFound: boolean;
  successMessage: string;
  isInsuredFieldsValid: boolean;
  isAgentFieldValid: boolean;
  isPolicyInfoValid: boolean;
  validationData: any;
  lossFormData: any;
  pageDataInitialized: boolean;
  formIsNotValid: boolean;
  modalMessage: string;
  lastClaimId: string;
  customClaimUrlString: string;
  claimsErrorTitle: string;
  claimsErrorMessage: string;
  claimsErrorType: "danger" | "warning" | null;
}

export default Vue.extend({
  name: "claims",
  mixins: [ActivitiesMixin],
  components: {
    LossFormClaimInfo,
    LossForm,
    CustomAlert
  },
  data(): IClaims {
    return {
      receivePaymentOption: "",
      hasValidCredentials: false,
      resourceNotFound: false,
      errorMessage: "",
      successMessage: "",
      isInsuredFieldsValid: false,
      isAgentFieldValid: false,
      isPolicyInfoValid: false,
      validationData: {},
      pageDataInitialized: false,
      lossFormData: {},
      formIsNotValid: true,
      modalMessage: "",
      lastClaimId: "",
      customClaimUrlString: "",
      claimsErrorMessage: "",
      claimsErrorTitle: "",
      claimsErrorType: null
    };
  },
  created() {
    this.initializePageData();
  },
  methods: {
    handleFormField(value: any) {
      this.formIsNotValid = value;
    },
    goToExistingClaim() {
      this.$router.push(`/lossformadmin/view/${this.lastClaimId}`);
      this.$modal.hide("lossFormModal");
    },
    async handleCreateExistingClaim() {
      await this.handleSuccessPolicyQuery(null, this.lossFormData);
    },
    async handleClaimsExceeded({
      message,
      lastClaimId,
      policyData,
      receivedClaimsData
    }: {
      message: string;
      lastClaimId: string;
      policyData: string;
      receivedClaimsData: Record<string, any>;
    }) {
      this.lossFormData = merge(
        cloneDeep(receivedClaimsData),
        this.lossFormData
      );
      this.modalMessage = message;
      this.lastClaimId = lastClaimId;
      this.customClaimUrlString = policyData;
      this.$modal.show("exceededClaims");
    },

    processLossData(data: any) {
      this.lossFormData = merge(cloneDeep(data), this.lossFormData);
      set(
        this.lossFormData,
        "lossFormRequire.paymentOption",
        get(
          this.lossFormData,
          "lossFormOtherInfo.FormInfo.paymentOption",
          ""
        ) || ""
      );

      if (!get(this.lossFormData, "formItem.email")) {
        set(
          this.lossFormData,
          "formItem.email",
          get(this.lossFormData, "lossFormOtherInfo.Info.Email")
        );
      }
      if (!get(this.lossFormData, "lossFormRequire.email")) {
        set(
          this.lossFormData,
          "lossFormRequire.email",
          get(this.lossFormData, "lossFormOtherInfo.Info.Email")
        );
      }
      if (!get(this.lossFormData, "lossFormRequire.email")) {
        set(
          this.lossFormData,
          "lossFormRequire.email",
          get(this.lossFormData, "lossFormOtherInfo.Info.Email")
        );
      }
      set(
        this.lossFormData,
        "companyPrefix",
        this.lossFormData?.lossFormOtherInfo?.Client?.PolicyNum?.split(" ")[0]
      );
      set(
        this.lossFormData,
        "agentCode",
        this.lossFormData?.formItem?.agentCode
      );
      set(
        this.lossFormData,
        "lossFormOtherInfo.Info.AgentCode",
        this.lossFormData?.formItem?.agentCode
      );
      set(
        this.lossFormData,
        "policyNumber",
        this.lossFormData?.lossFormOtherInfo?.Client?.PolicyNum
      );
      set(this.lossFormData, "site", "ATLAS");
      if (this.claimId) {
        set(this.lossFormData, "claimId", this.claimId);
      }
      set(this.lossFormData, "role", this.$getCurrentUser?.role);
      const { BranchName = "", Branch = "" } =
        this.lossFormData?.lossFormOtherInfo?.Coverages || {};
      set(
        this.lossFormData,
        "lossFormOtherInfo.Coverages.FormType",
        `${Branch} - ${BranchName}`
      );
      set(this.lossFormData, "lossFormRequire.formType", BranchName);

      set(
        this.lossFormData,
        "lossFormRequire.policyNum",
        this.lossFormData?.policyNumber
      );
      set(
        this.lossFormData,
        "lossFormRequire.refAgentCode",
        this.lossFormData?.formItem?.agentCode
      );
      this.lossFormData.lossFormRequire = {
        ...this.lossFormData.lossFormRequire,
        ...this.lossFormData.formItem
      };
    },
    async sendLossDataToFiserv(lossData: any) {
      this.$modal.hide("exceededClaims");
      this.processLossData(lossData);
      const { data, ...rest } = await this.createClaims({
        data: this.lossFormData
      });
      this.lossFormData = merge(cloneDeep(data), this.lossFormData, rest);
    },
    async handleSuccessPolicyQuery(policyData: any, lossData: any) {
      await this.sendLossDataToFiserv(lossData);
    },
    ...claimsMapActions(["updateClaims", "getClaim", "createClaims"]),
    ...claimsMapMutations({
      editField: types.SET_EDIT_FIELD,
      setEdit: types.SET_EDIT
    }),
    getValue(dataObject: object, key: string): string {
      return get(dataObject, key, "") || "";
    },
    addHardCodedFields(): void {
      //Fields we are legally required to hardcode
      this.lossFormData.role = this.$getCurrentUser.role;
      this.lossFormData.agentCode = this.lossFormData.formItem.agentCode;
      this.lossFormData.site = "ATLAS";
      this.lossFormData["policyNumber"] =
        get(this.lossFormData, "lossFormOtherInfo.Client.PolicyNum", "") || "";
      this.lossFormData["companyPrefix"] = this.lossFormData.policyNumber.split(
        " "
      )[0];
      this.lossFormData.isEditing = true;
    },
    async initializePageData() {
      try {
        await this.getClaim(this.claimId);
        const { data, ...rootFields } = this.editing as any;
        this.lossFormData = { ...rootFields, ...data };
        this.lossFormData.mortgageInfo = getLossMortgageAddress(
          this.lossFormData
        );
        this.addHardCodedFields();
        this.pageDataInitialized = true;
      } catch (error) {
        this.resourceNotFound = true;
        this.$bugSnagClient.notify(error);
      }
    },
    async formFieldChangedHandler({ key, value }: { key: string; value: any }) {
      const dateFields = [
        "lossFormOtherInfo.FormInfo.LossDate",
        "lossFormOtherInfo.Coverages.DateAssigned"
      ];
      if (value instanceof Date || dateFields.includes(key)) {
        set(
          this.lossFormData,
          key,
          dateFns.format(value, LEGACY_PHP_API_DATE_FORMAT)
        );
      } else if (key === "lossFormOtherInfo.FormInfo.paymentOption") {
        await processPaymentOptionEdit.bind(this)(
          key,
          value,
          this.lossFormData
        );
      } else if (
        key === "lossFormOtherInfo.FormInfo.ClassId" &&
        value !== "21"
      ) {
        set(this.lossFormData, "lossFormOtherInfo.FormInfo.ClaimAntName", "");
        set(this.lossFormData, "lossFormOtherInfo.FormInfo.ClaimAntPhone", "");
        set(
          this.lossFormData,
          "lossFormOtherInfo.FormInfo.ClaimAntAddress",
          ""
        );
        set(this.lossFormData, key, value);
      } else {
        set(this.lossFormData, key, value);
      }
      this.lossFormData = cloneDeep(this.lossFormData);
    },
    async updateLossForm(_type = "update"): Promise<void> {
      this.errorMessage = "";
      try {
        if (
          !this.lossFormData?.lossFormRequire?.claimNum ||
          !this.lossFormData?.lossFormOtherInfo?.Info?.Claim
        ) {
          await this.sendLossDataToFiserv({});
        }
        const toOmit = ["mortgageInfo"];
        if (_type === "VS" || _type === "VR") {
          this.lossFormData["brk_pt"] = _type;
        }
        set(
          this.lossFormData,
          "lossFormRequire.paymentOption",
          get(
            this.lossFormData,
            "lossFormOtherInfo.FormInfo.paymentOption",
            ""
          ) || ""
        );

        if (
          !lossDateIsGreaterThan120Days(
            this.getValue(
              this.lossFormData,
              "lossFormOtherInfo.FormInfo.LossDate"
            )
          ) &&
          get(
            this.lossFormData,
            "lossFormOtherInfo.FormInfo.ReasonForDelayInReport",
            ""
          )
        ) {
          this.lossFormData.lossFormOtherInfo.FormInfo.ReasonForDelayInReport =
            "";
        }

        this.lossFormData.lossFormRequire = {
          ...this.lossFormData.lossFormRequire,
          ...this.lossFormData.formItem
        };

        await this.updateClaims({
          id: this.claimId,
          update: omit(this.lossFormData, toOmit)
        });
        this.$appNotifySuccess("Loss Form updated.");
        this.$router.push("/claims").catch(() => {});
      } catch (e) {
        this.errorMessage = e.message || "Error Submitting Claims";
      } finally {
        this.$emit("scrollTop");
      }
    },
    async onToolbarItemSelected(action: string): Promise<void> {
      if (action === "update") {
        this.updateLossForm();
      }
      if (action === "VS" || action === "VR") {
        this.updateLossForm(action);
      }

      if (action === "print") {
        await downloadClaimsDocument({
          _ids: [this.lossFormData._id],
          policyPrefix: this.lossFormData.companyPrefix
        });
      }
      if (action === "cancel") {
        this.$router.go(-1);
      }
      if (action === "getPolicyInformation") {
        (this.$refs.lossFormComponent as any).createLossForm();
      }
    },
    showCustomAlert() {
      if (this.lossFormData?.policyStatus) {
        this.claimsErrorType = "warning";
        this.claimsErrorMessage = this.lossFormData?.policyStatusMessage;
        this.claimsErrorTitle = "Claims Exists";
        return true;
      } else if (this.errorMessage) {
        this.claimsErrorType = "danger";
        this.claimsErrorMessage = this.errorMessage;
        this.claimsErrorTitle = "Claims Error";
        return true;
      }
      return false;
    }
  },
  computed: {
    ...claimsMapState(["editing", "makingApiRequest"]),
    ...authMapGetters(["getCurrentUser"]),
    dynamicFormBuilderOptions(): any {
      return {
        "lossFormOtherInfo.FormInfo.ClassId": [...KindOfLossOptions],
        "lossFormOtherInfo.Coverages.FieldAssigned": [...FieldAdjusterOptions],
        "lossFormOtherInfo.Coverages.ClaimRepresentative": [
          ...AtlasClaimRepresentativeOptions
        ]
      };
    },
    claimId(): string {
      return this.$route.params._id;
    },
    fieldsWithIssues(): string[] {
      if (this.validationData.fieldsWithErrors) {
        return Object.values(this.validationData.fieldLabels);
      }
      return [];
    },
    lossEditActions(): any[] {
      const actions = [
        { title: "Cancel", command: "cancel" },
        { title: "Print", command: "print" }
      ];
      if (this.getCurrentUser?.role === "admin") {
        actions.push({ title: "View Sending XML", command: "VS" });
        actions.push({ title: "View Receiving XML", command: "VR" });
      }
      return actions;
    },
    claimFormFields(): any[] {
      let viewingState = this.editing?.deleted ? "viewing" : "editing";
      return claimsFormSchema({
        type: "update",
        viewType: viewingState,
        otherAuthConcact: this.lossFormData.lossFormOtherInfo.Client
          .OtherAuthConcact,
        otherPhone: this.lossFormData.lossFormOtherInfo.Client.OtherPhone,
        windExcludeStatement: this.lossFormData.lossFormOtherInfo.Coverages
          .WindExcludeStatement,
        showReasonFormDelayInReport: lossDateIsGreaterThan120Days(
          this.getValue(
            this.lossFormData,
            "lossFormOtherInfo.FormInfo.LossDate"
          )
        )
      });
    },
    queryOverride(): any {
      return {
        "data.claimsRequestId": this.editing?._id,
        activityType: "ClaimsRequestActivity"
      };
    },
    claimsHasValidPolicyInformation(): boolean {
      const policyInfo = get(
        this.lossFormData,
        "lossFormOtherInfo.Client.PolicyNum",
        ""
      );
      const [policyPrefix = "", policyNumber = ""] = policyInfo.split(" ");
      return Boolean(
        policyPrefix && policyNumber && this.lossFormData?.compNum
      );
    },
    topActionButtonPrimary(): any {
      if (!this.claimsHasValidPolicyInformation) {
        return {
          text: "Get Policy Information",
          key: "getPolicyInformation",
          disabled: this.formIsNotValid,
          loading: this.makingApiRequest
        };
      }
      return {
        text: "Update",
        key: "update",
        disabled: this.fieldsWithIssues.length,
        loading: this.makingApiRequest
      };
    }
  }
});
